import { Layers } from "lucide-react";
import type { AggregateRow } from "../lib/api";
import { tagMeta } from "../lib/taxonomy";

export function AggregatesTable({ rows }: { rows: AggregateRow[] }) {
  return (
    <section className="rounded-lg border border-zinc-800 bg-zinc-900/40">
      <header className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <Layers className="w-4 h-4 text-brand-400" />
        <h2 className="text-sm font-semibold">Recent aggregates</h2>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">
          {rows.length} bucket{rows.length === 1 ? "" : "s"}
        </span>
      </header>

      {rows.length === 0 ? (
        <div className="p-10 text-center text-zinc-500 text-sm">
          No aggregates yet.
        </div>
      ) : (
        <div className="overflow-auto max-h-[420px]">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-2 font-medium">Bucket</th>
                <th className="px-4 py-2 font-medium">Planogram</th>
                <th className="px-4 py-2 font-medium">Device</th>
                <th className="px-4 py-2 font-medium">k</th>
                <th className="px-4 py-2 font-medium">Tags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-900/60">
                  <td className="px-4 py-3 align-top text-xs text-zinc-400 tabular-nums whitespace-nowrap">
                    {fmt(r.bucket_start)}
                  </td>
                  <td className="px-4 py-3 align-top text-xs font-mono text-zinc-300">
                    {r.planogram_id}
                  </td>
                  <td className="px-4 py-3 align-top text-xs font-mono text-zinc-400">
                    {r.device_id}
                  </td>
                  <td className="px-4 py-3 align-top text-xs tabular-nums text-zinc-400">
                    {r.k_threshold}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-wrap gap-1">
                      {r.tags.map((t) => {
                        const m = tagMeta(t.tag_id);
                        const tone =
                          m.polarity === "positive" ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" :
                          m.polarity === "negative" ? "text-rose-300 border-rose-500/30 bg-rose-500/10" :
                                                      "text-amber-300 border-amber-500/30 bg-amber-500/10";
                        return (
                          <span key={t.tag_id} className={`px-2 py-0.5 rounded text-[11px] border ${tone}`}>
                            {m.label} ×{t.count}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function fmt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}
